import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Search, CheckCircle2, ArrowRight } from "lucide-react";
import { listBlogPosts } from "@/lib/api";
import SiteVisitForm from "@/components/SiteVisitForm";

const BLOG_IMG =
  "https://images.pexels.com/photos/4062267/pexels-photo-4062267.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let mounted = true;
    listBlogPosts()
      .then((data) => mounted && setPosts(data))
      .catch(() => mounted && setPosts([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!q) return posts;
    const needle = q.toLowerCase();
    return posts.filter(
      (p) =>
        p.title.toLowerCase().includes(needle) ||
        (p.excerpt || "").toLowerCase().includes(needle) ||
        (p.category || "").toLowerCase().includes(needle)
    );
  }, [posts, q]);

  return (
    <div data-testid="blog-page" className="text-white">
      <Seo />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-radial-fade" />
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center rounded-full border border-[#FE7200]/30 bg-[#FE7200]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#FE7200]">
              Anytime Vending Machines
            </div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              <span className="text-[#FE7200]">Blog</span> · Tips, Updates & Vending Guides
            </h1>
            <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl">
              Helpful vending tips, eligibility guides, and product ideas — plus you can book a free site visit below.
            </p>

            <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-300">
              {["Free installs (qualifying sites)", "Fully stocked & serviced", "Tap & Go + cash/coin", "Country-wide"].map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#FE7200]" /> {b}
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="#latest-posts"
                data-testid="blog-read-latest"
                className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
              >
                Read The Latest
              </a>
              <a
                href="#book-form"
                data-testid="blog-book-button"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-white/10 transition-colors"
              >
                Book A Free Site Visit
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#121212]">
              <img src={BLOG_IMG} alt="Vending machine snacks" className="w-full h-80 lg:h-96 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      <section id="latest-posts" className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20" data-testid="latest-posts-section">
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Latest posts</h2>
        <p className="mt-3 text-zinc-400 text-sm">Posts load automatically from our CMS.</p>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6" data-testid="posts-list">
            {loading && <div className="text-zinc-400">Loading posts…</div>}
            {!loading && filtered.length === 0 && (
              <div data-testid="no-posts" className="rounded-xl border border-white/10 bg-[#121212] p-6 text-zinc-300">
                No posts match your search.
              </div>
            )}
            {filtered.map((p, i) => (
              <article
                key={p.id}
                data-testid={`post-card-${i}`}
                className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7 hover:border-[#FE7200]/40 transition-colors"
              >
                <div className="flex items-center gap-3 text-xs">
                  <span className="inline-flex items-center rounded-full bg-[#FE7200] px-3 py-1 font-bold uppercase tracking-[0.18em] text-black">
                    {p.category || "Updates"}
                  </span>
                  <span className="text-zinc-500">·</span>
                  <span className="text-zinc-400">
                    {new Date(p.created_at).toLocaleDateString("en-US", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <h3 className="mt-3 font-display text-xl md:text-2xl font-bold text-white leading-snug">
                  <Link to={`/blog/${p.slug}`} data-testid={`post-title-link-${i}`} className="hover:text-[#FE7200]">
                    {p.title}
                  </Link>
                </h3>
                <p className="mt-2 text-sm md:text-base text-zinc-300 leading-relaxed">
                  {p.excerpt}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`post-read-more-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#FE7200] px-5 py-2 text-xs font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
                  >
                    Read More <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <a
                    href="#book-form"
                    data-testid={`post-book-${i}`}
                    className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-5 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-white/10 transition-colors"
                  >
                    Book Free Site Visit
                  </a>
                </div>
              </article>
            ))}
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="search-card">
              <h3 className="font-display text-lg font-bold text-white">Search posts</h3>
              <p className="text-xs text-zinc-400 mt-1">Type to filter by keyword, category or tag.</p>
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <input
                  data-testid="blog-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search… e.g. eligibility, products, $0"
                  className="w-full rounded-lg bg-[#0f0f10] border border-white/10 pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FE7200] focus:ring-2 focus:ring-[#FE7200]/30"
                />
              </div>
            </div>

            <div className="rounded-2xl border border-[#FE7200]/30 bg-[#FE7200]/5 p-6" data-testid="free-machine-card">
              <h3 className="font-display text-lg font-bold text-white">Want a free machine?</h3>
              <p className="mt-2 text-sm text-zinc-300">
                If you have <b className="text-white">30+ staff</b> or steady foot traffic, you may qualify for a <b className="text-white">$0</b> install.
              </p>
              <a
                href="#book-form"
                data-testid="sidebar-book-button"
                className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-[#FE7200] px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
              >
                Book Free Site Visit
              </a>
              <p className="mt-3 text-xs text-zinc-400">We'll confirm eligibility and recommend the best setup.</p>
            </div>
          </aside>
        </div>
      </section>

      <section id="book-form" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-10 md:py-16 border-t border-white/5" data-testid="blog-form-section">
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Book a Free Site Visit</h2>
        <p className="mt-3 text-zinc-300">Fill this out and we'll confirm eligibility and contact you to arrange the best time.</p>
        <div className="mt-8">
          <SiteVisitForm source="blog-booking" testIdPrefix="blog-booking" />
        </div>
      </section>
    </div>
  );
}
